import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Plus, Loader2, Trash2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface Props {
  nurseryId: string;
  lessons: { id: string; title: string }[];
  onSuccess: () => void;
  trigger?: React.ReactNode;
}

const subjects = [
  { value: "عربي", label: "اللغة العربية" },
  { value: "رياضيات", label: "الرياضيات" },
  { value: "انجليزي", label: "اللغة الإنجليزية" },
  { value: "علوم", label: "العلوم" },
  { value: "اسلامي", label: "التربية الإسلامية" },
];

const stages = [
  { value: "kg1", label: "KG1" },
  { value: "kg2", label: "KG2" },
  { value: "grade1", label: "أولى ابتدائي" },
  { value: "grade2", label: "ثانية ابتدائي" },
  { value: "grade3", label: "ثالثة ابتدائي" },
];

export default function AddQuestionDialog({ nurseryId, lessons, onSuccess, trigger }: Props) {
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const [questionText, setQuestionText] = useState("");
  const [questionType, setQuestionType] = useState<string>("multiple_choice");
  const [difficulty, setDifficulty] = useState<string>("medium");
  const [subject, setSubject] = useState<string>("");
  const [stage, setStage] = useState<string>("");
  const [lessonId, setLessonId] = useState<string>("");
  const [correctAnswer, setCorrectAnswer] = useState("");
  const [explanation, setExplanation] = useState("");
  const [options, setOptions] = useState(["", "", "", ""]);

  const resetForm = () => {
    setQuestionText("");
    setQuestionType("multiple_choice");
    setDifficulty("medium");
    setSubject("");
    setStage("");
    setLessonId("");
    setCorrectAnswer("");
    setExplanation("");
    setOptions(["", "", "", ""]);
  };

  const handleSave = async () => {
    if (!questionText.trim() || !correctAnswer.trim()) {
      toast({ title: "خطأ", description: "يرجى ملء نص السؤال والإجابة الصحيحة", variant: "destructive" });
      return;
    }

    if (questionType === "multiple_choice") {
      const filledOptions = options.filter((o) => o.trim());
      if (filledOptions.length < 2) {
        toast({ title: "خطأ", description: "أضف خيارين على الأقل", variant: "destructive" });
        return;
      }
      if (!filledOptions.includes(correctAnswer.trim())) {
        toast({ title: "خطأ", description: "الإجابة الصحيحة يجب أن تكون من ضمن الخيارات", variant: "destructive" });
        return;
      }
    }

    setSaving(true);
    try {
      const { error } = await supabase.from("questions").insert({
        nursery_id: nurseryId,
        question_text: questionText.trim(),
        question_type: questionType as any,
        difficulty: difficulty as any,
        correct_answer: correctAnswer.trim(),
        explanation: explanation.trim() || null,
        subject: subject || null,
        lesson_id: (lessonId && lessonId !== "none") ? lessonId : null,
        is_ai_generated: false,
        options: questionType === "multiple_choice" ? options.filter((o) => o.trim()) : null,
      });
      if (error) throw error;

      toast({ title: "✅ تم إضافة السؤال بنجاح" });
      resetForm();
      onSuccess();
      setOpen(false);
    } catch (e: any) {
      toast({ title: "خطأ", description: e.message, variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) resetForm(); }}>
      <DialogTrigger asChild>
        {trigger || (
          <Button size="sm">
            <Plus className="w-4 h-4 ml-1" />
            إضافة سؤال
          </Button>
        )}
      </DialogTrigger>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Plus className="w-5 h-5 text-primary" />
            إضافة سؤال يدوي
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Question text */}
          <div>
            <Label>نص السؤال *</Label>
            <Textarea
              value={questionText}
              onChange={(e) => setQuestionText(e.target.value)}
              placeholder="اكتب نص السؤال هنا..."
              className="mt-1"
              rows={3}
            />
          </div>

          {/* Type & Difficulty */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>نوع السؤال</Label>
              <Select value={questionType} onValueChange={(v) => { setQuestionType(v); setCorrectAnswer(""); }}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="multiple_choice">اختيار من متعدد</SelectItem>
                  <SelectItem value="true_false">صح وخطأ</SelectItem>
                  <SelectItem value="essay">مقالي</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>مستوى الصعوبة</Label>
              <Select value={difficulty} onValueChange={setDifficulty}>
                <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="easy">🟢 سهل</SelectItem>
                  <SelectItem value="medium">🟡 متوسط</SelectItem>
                  <SelectItem value="hard">🔴 صعب</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Subject & Stage */}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>المادة</Label>
              <Select value={subject} onValueChange={setSubject}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="اختر المادة" /></SelectTrigger>
                <SelectContent>
                  {subjects.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>المرحلة</Label>
              <Select value={stage} onValueChange={setStage}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="اختر المرحلة" /></SelectTrigger>
                <SelectContent>
                  {stages.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Lesson */}
          {lessons.length > 0 && (
            <div>
              <Label>الدرس (اختياري)</Label>
              <Select value={lessonId} onValueChange={setLessonId}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="ربط بدرس" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">بدون درس</SelectItem>
                  {lessons.map((l) => (
                    <SelectItem key={l.id} value={l.id}>{l.title}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Options for multiple choice */}
          {questionType === "multiple_choice" && (
            <div>
              <Label>الخيارات</Label>
              <div className="space-y-2 mt-1">
                {options.map((opt, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <Input
                      value={opt}
                      onChange={(e) => {
                        const newOpts = [...options];
                        newOpts[i] = e.target.value;
                        setOptions(newOpts);
                      }}
                      placeholder={`الخيار ${i + 1}`}
                      className="flex-1"
                    />
                    {options.length > 2 && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="h-9 w-9 shrink-0"
                        onClick={() => setOptions(options.filter((_, j) => j !== i))}
                      >
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    )}
                  </div>
                ))}
                {options.length < 6 && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setOptions([...options, ""])}
                    className="w-full"
                  >
                    <Plus className="w-4 h-4 ml-1" />
                    إضافة خيار
                  </Button>
                )}
              </div>
            </div>
          )}

          {/* Correct answer */}
          <div>
            <Label>الإجابة الصحيحة *</Label>
            {questionType === "true_false" ? (
              <Select value={correctAnswer} onValueChange={setCorrectAnswer}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="اختر الإجابة" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="true">صح ✓</SelectItem>
                  <SelectItem value="false">خطأ ✗</SelectItem>
                </SelectContent>
              </Select>
            ) : questionType === "multiple_choice" ? (
              <Select value={correctAnswer} onValueChange={setCorrectAnswer}>
                <SelectTrigger className="mt-1"><SelectValue placeholder="اختر الإجابة الصحيحة" /></SelectTrigger>
                <SelectContent>
                  {options.filter((o) => o.trim()).map((o, i) => (
                    <SelectItem key={i} value={o}>{o}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Textarea
                value={correctAnswer}
                onChange={(e) => setCorrectAnswer(e.target.value)}
                placeholder="اكتب الإجابة الصحيحة..."
                className="mt-1"
                rows={2}
              />
            )}
          </div>

          {/* Explanation */}
          <div>
            <Label>شرح الإجابة (اختياري)</Label>
            <Textarea
              value={explanation}
              onChange={(e) => setExplanation(e.target.value)}
              placeholder="شرح مبسط للإجابة..."
              className="mt-1"
              rows={2}
            />
          </div>

          <Button
            onClick={handleSave}
            disabled={saving || !questionText.trim() || !correctAnswer.trim()}
            className="w-full h-11"
          >
            {saving ? (
              <><Loader2 className="w-4 h-4 animate-spin ml-2" />جاري الحفظ...</>
            ) : (
              <><Plus className="w-4 h-4 ml-2" />حفظ السؤال</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
